/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import {getAccountId} from '@ion/applications/src/helpers';
import {
  errorAddOptions,
  errorEditOptions,
  successAddOptions,
  successEditOptions
} from '@ion/applications/src/prompts/constants';
import {error} from '@ion/core/src/logger/logger';
import {
  createApplication,
  deleteApplication,
  deleteApplications,
  getApplication,
  loadApplications,
  updateApplication
} from '@ion/core/src/services/Connect';
import {
  Application,
  ApplicationFilters,
  Applications,
  AuditChange,
  Entity
} from '@ion/core/src/types/entities';
import {LoadApplicationsParams, ServiceError} from '@ion/core/src/types/services';
import {getSearchableFilters} from '@ion/features/src/search/utils';
import {retryUpdate} from '@ion/services/src/helpers/retry';
import {PageInitializeAction} from '@/types/redux';
import {toggleUserPrompt} from './user-prompt';

;

export const NAME                                        = 'adminApplications';

export const INITIALIZE_ADMIN_APPLICATIONS               = 'INITIALIZE_ADMIN_APPLICATIONS';
export const ADMIN_APPLICATIONS_GRID_REFRESHED           = 'ADMIN_APPLICATIONS_GRID_REFRESHED';
export const SET_GRID_COLUMNS                            = 'SET_ADMIN_APPLICATIONS_GRID_COLUMNS';
export const SET_VIEW_SUB_GROUPS                         = 'SET_ADMIN_APPLICATIONS_SUB_GROUPS';
export const SET_GRID_PAGE_SIZE                          = 'SET_ADMIN_APPLICATIONS_GRID_PAGE_SIZE';
export const UPDATE_ADMIN_APPLICATIONS_GRID_COLUMNS      = 'UPDATE_ADMIN_APPLICATIONS_GRID_COLUMNS';
export const GET_ADMIN_APPLICATIONS_REQUEST              = 'GET_ADMIN_APPLICATIONS_REQUEST';
export const GET_ADMIN_APPLICATIONS_RESPONSE             = 'GET_ADMIN_APPLICATIONS_RESPONSE';
export const GET_ADMIN_APPLICATIONS_ERROR                = 'GET_ADMIN_APPLICATIONS_ERROR';
export const UPDATE_APPLICATIONS_SEARCH_QUERIES          = 'UPDATE_APPLICATIONS_SEARCH_QUERIES';
export const RESET_APPLICATIONS_SEARCH_QUERIES           = 'RESET_APPLICATIONS_SEARCH_QUERIES';
export const SHOW_ADD_APPLICATION_DIALOG                 = 'SHOW_ADD_APPLICATION_DIALOG';
export const ADD_ADMIN_APPLICATION_REQUEST               = 'ADD_ADMIN_APPLICATION_REQUEST';
export const ADD_ADMIN_APPLICATION_RESPONSE              = 'ADD_ADMIN_APPLICATION_RESPONSE';
export const ADD_ADMIN_APPLICATION_ERROR                 = 'ADD_ADMIN_APPLICATION_ERROR';
export const DISPLAY_DELETE_APPLICATIONS_DIALOG          = 'DISPLAY_DELETE_APPLICATIONS_DIALOG';
export const DELETE_ADMIN_APPLICATIONS_REQUEST           = 'DELETE_ADMIN_APPLICATIONS_REQUEST';
export const DELETE_ADMIN_APPLICATIONS_RESPONSE          = 'DELETE_ADMIN_APPLICATIONS_RESPONSE';
export const DELETE_ADMIN_APPLICATIONS_ERROR             = 'DELETE_ADMIN_APPLICATIONS_ERROR';
export const SHOW_EDIT_APPLICATION_DIALOG                = 'SHOW_EDIT_APPLICATION_DIALOG';
export const EDIT_ADMIN_APPLICATION_REQUEST              = 'EDIT_ADMIN_APPLICATION_REQUEST';
export const EDIT_ADMIN_APPLICATION_RESPONSE             = 'EDIT_ADMIN_APPLICATION_RESPONSE';
export const EDIT_ADMIN_APPLICATION_ERROR                = 'EDIT_ADMIN_APPLICATION_ERROR';
export const AUDIT_ADMIN_APPLICATION                     = 'AUDIT_ADMIN_APPLICATION';
export const AUDIT_ADMIN_APPLICATION_SHOW_CHANGES_DIALOG = 'AUDIT_ADMIN_APPLICATION_SHOW_CHANGES_DIALOG';

/**
 * initialize Applications
 */
export const initializeAdminApplications = (): PageInitializeAction => {
  return {type: INITIALIZE_ADMIN_APPLICATIONS};
};

/**
 * set Grid Columns
 */
export const setGridColumns = (columnState: ColumnState[]) => ({
  type : SET_GRID_COLUMNS, columnState
});

/**
 * Called when Grid Refreshed
 */
export const adminApplicationsGridRefreshed = (): PageInitializeAction => ({
  type : ADMIN_APPLICATIONS_GRID_REFRESHED
});

/**
 * Called to update the modular search queries to the application state
 */
export const updateApplicationsSearchQueries = (searchParams: ApplicationFilters) => ({
  type : UPDATE_APPLICATIONS_SEARCH_QUERIES, searchParams
});

/**
 * Called to Reset the application filters state
 */
export const resetApplicationsSearchQueries = () => ({
  type : RESET_APPLICATIONS_SEARCH_QUERIES
});

/**
 * Invoked whenever user click on View Sub Accounts toggle button
 */
export const setApplicationViewSubGroups = (viewSubGroups: boolean = false) => ({
  type : SET_VIEW_SUB_GROUPS, viewSubGroups
});

/**
 * Invoked when the the Admin Applications auditor is to be displayed or removed.
 */
export const auditAdminApplications = (applications: Application, show = true) => ({
  type : AUDIT_ADMIN_APPLICATION, applications, show
});

export const showAuditAdminApplicationsChangesDialog = (auditRecord: AuditChange, show = true) => ({
  type : AUDIT_ADMIN_APPLICATION_SHOW_CHANGES_DIALOG, auditRecord, show
});

/**
 * Retrieve selected Applications details
 */
export const getAdminApplications = ({
  sort,
  callback: callbackFn,
  pageId = 1,
  pageSize = 20,
  includeSubGroups = false
}) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    const {
      adminApplications: {
        search
      }
    } = getState();
    dispatch({type: GET_ADMIN_APPLICATIONS_REQUEST});
    const data: LoadApplicationsParams = {
      accountId   : getAccountId(getState),
      filterQuery : getSearchableFilters(search),
      pageId,
      pageSize,
      includeSubGroups
    };
    if (sort) {
      data.sortOn = sort;
    }
    try {
      const {application: items, totalCount} = await loadApplications(data);
      dispatch({type: GET_ADMIN_APPLICATIONS_RESPONSE});
      callbackFn?.({items, totalCount});
      return {items, totalCount};
    } catch (err) {
      error(err);
      callbackFn?.({error: err, items: [], totalCount: 0});
      dispatch({type: GET_ADMIN_APPLICATIONS_ERROR, error: err});
      return err;
    }
  };
};

// Opens or closes the add dialog for Admin Applications
export const showAddApplicationDialog = (showDialog: boolean = true) => ({
  type : SHOW_ADD_APPLICATION_DIALOG, showDialog
});

// Dispatches actions related to creating the given Application
export const addApplication = (application: Application) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: ADD_ADMIN_APPLICATION_REQUEST});
    try {
      const addedApplication = await createApplication(application);
      dispatch({type: ADD_ADMIN_APPLICATION_RESPONSE, applications: addedApplication});
      dispatch(toggleUserPrompt(successAddOptions(Entity.application)));
    } catch (err) {
      dispatch({type: ADD_ADMIN_APPLICATION_ERROR, error: err});
      dispatch(toggleUserPrompt(errorAddOptions(Entity.application, err)));
      error(err);
    }
  };
};

// Dispatches an action to show or hide the Application deletion confirmation dialog to the user
export const displayDeleteDialog = (applications: Application | Applications, show = true) => ({
  type : DISPLAY_DELETE_APPLICATIONS_DIALOG, applications, show
});

// Dispatches actions related to deleting a single or multiple (bulk) Applications
export const deleteAdminApplications = (applications: Application | Applications) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: DELETE_ADMIN_APPLICATIONS_REQUEST});
    try {
      const service = Array.isArray(applications) ? deleteApplications : deleteApplication;
      const deletedApplications = await service(applications as Application & Applications);
      if (deletedApplications?.errors && deletedApplications.errors.length > 0) {
        dispatch({type: DELETE_ADMIN_APPLICATIONS_ERROR, error: deletedApplications.errors});
        return;
      }
      dispatch({type: DELETE_ADMIN_APPLICATIONS_RESPONSE, applications: deletedApplications});
    } catch (err) {
      dispatch({type: DELETE_ADMIN_APPLICATIONS_ERROR, error: err});
      error(err);
    }
  };
};

// Shows or hides the Applications edit dialog
export const showEditApplicationDialog = (
  applications: Application,
  showDialog: boolean = true,
  isView: boolean = false
) => ({
  type : SHOW_EDIT_APPLICATION_DIALOG, applications, showDialog, isView
});

// Handles the dispatching of actions related to editing a PEG Label
export const editApplication = (application: Application) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: EDIT_ADMIN_APPLICATION_REQUEST});
    try {
      const applications = await retryUpdate({
        newEntity     : application,
        updateEntity  : updateApplication,
        getEntity     : getSingleApplication,
        onError       : applicationUpdateError,
        isApplication : true,
        dispatch
      });
      if (applications) {
        dispatch({type: EDIT_ADMIN_APPLICATION_RESPONSE, applications});
        dispatch(toggleUserPrompt(successEditOptions(Entity.application)));
      }
    } catch (err) {
      applicationUpdateError(err, dispatch);
    }
  };
};

/**
 * Invoked when an existing Application gets resource out of state, delegating to the fetch
 * latest application details successful response or error action accordingly.
 */
const getSingleApplication = async(applicationKey: string) => {
  try {
    const application = await getApplication(applicationKey);
    return {updated: application, error: ''};
  } catch (err) {
    error(err);
    return {updated: null, error: err};
  }
};

/**
 * Invoked when an existing Application gets updated or fetched
 * and dispatching a successful or error action accordingly.
 */
const applicationUpdateError = (err: ServiceError, dispatch: Function) => {
  dispatch({type: EDIT_ADMIN_APPLICATION_ERROR, error: err});
  dispatch(toggleUserPrompt(errorEditOptions(Entity.application, err)));
  error(err);
};

/**
 * Invoked whenever user change the row per page dropdown
 */
export const setAdminApplicationsGridPageSize = (pageSize: number) => ({
  type : SET_GRID_PAGE_SIZE, pageSize
});
